import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';

import { parseStyle } from './cssStringToReactStyle.ts';
import { i18n } from './iframeMacroI18n.ts';

// Legacy parity: this combines allow-same-origin with allow-scripts, which weakens sandbox
// isolation because same-origin framed content can remove its sandbox attribute. Follow up on
// whether allow-same-origin can be removed without breaking legacy iframe macro content.
const SANDBOX_ALLOW_LIST =
	'allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-top-navigation-by-user-activation';

type MacroParamValue = string | number | boolean | null | undefined;
type MacroParam = {
	value?: MacroParamValue;
};

type IFrameAttributes = React.IframeHTMLAttributes<HTMLIFrameElement> & {
	align?: string;
	longdesc?: string;
};

export type IFrameMacroParams = {
	src?: MacroParam;
	id?: MacroParam;
	class?: MacroParam;
	style?: MacroParam;
	name?: MacroParam;
	title?: MacroParam;
	width?: MacroParam;
	height?: MacroParam;
	align?: MacroParam;
	longdesc?: MacroParam;
	marginheight?: MacroParam;
	marginwidth?: MacroParam;
	scrolling?: MacroParam;
	dir?: MacroParam;
	lang?: MacroParam;
	frameborder?: MacroParam;
	allowfullscreen?: MacroParam;
};

export type IFrameMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: IFrameMacroParams;
};

const renderErrorFallback = () => <FormattedMessage {...i18n.error} />;

const getParamValue = (param?: MacroParam): string | undefined => {
	if (param?.value === null || param?.value === undefined) {
		return undefined;
	}

	if (param.value === '') {
		return undefined;
	}

	return String(param.value);
};

const getNumberParamValue = (param?: MacroParam): number | undefined => {
	const value = getParamValue(param);

	if (value === undefined) {
		return undefined;
	}

	const numberValue = Number(value);

	return Number.isNaN(numberValue) ? undefined : numberValue;
};

export const getFrameBorder = (frameBorder?: string): string =>
	!frameBorder || frameBorder === 'show' ? '1' : '0';

// Preserve the legacy Velocity check: any non-empty value except the exact string "false"
// renders the boolean allowfullscreen attribute.
export const getAllowFullScreen = (allowFullScreen?: string): boolean =>
	Boolean(allowFullScreen && allowFullScreen !== 'false');

const IFrameMacroRendererInner = ({ macroDefaultProps, macroParams }: IFrameMacroRendererProps) => {
	const intl = useIntl();
	const macroId = macroDefaultProps?.attributes?.macroId;
	const title = getParamValue(macroParams?.title) || intl.formatMessage(i18n.embeddedContentTitle);
	const longDescription = getParamValue(macroParams?.longdesc);
	const marginWidth = getNumberParamValue(macroParams?.marginwidth);
	const style = parseStyle(getParamValue(macroParams?.style));
	const marginHeight = getNumberParamValue(macroParams?.marginheight);
	const className = getParamValue(macroParams?.class);
	const align = getParamValue(macroParams?.align);
	const iframeAttributes: IFrameAttributes = {
		src: getParamValue(macroParams?.src),
		dir: getParamValue(macroParams?.dir),
		scrolling: getParamValue(macroParams?.scrolling),
		name: getParamValue(macroParams?.name),
		width: getParamValue(macroParams?.width),
		id: getParamValue(macroParams?.id),
		height: getParamValue(macroParams?.height),
		lang: getParamValue(macroParams?.lang),
		frameBorder: getFrameBorder(getParamValue(macroParams?.frameborder)),
		allowFullScreen: getAllowFullScreen(getParamValue(macroParams?.allowfullscreen)),
		sandbox: SANDBOX_ALLOW_LIST,
		...(align ? { align } : {}),
		...(longDescription ? { longdesc: longDescription } : {}),
		...(marginWidth !== undefined ? { marginWidth } : {}),
		...(style ? { style } : {}),
		...(marginHeight !== undefined ? { marginHeight } : {}),
		...(className ? { className } : {}),
	};

	return (
		<div data-testid="iframe-macro" data-fabric-macro={macroId} data-macro-body="">
			<iframe title={title} {...iframeAttributes} />
		</div>
	);
};

export const IFrameMacroRenderer = (props: IFrameMacroRendererProps): React.JSX.Element => (
	<GenericErrorBoundary
		renderOnError={renderErrorFallback}
		attribution={Attribution.PAGE_EXPERIENCES}
	>
		<IFrameMacroRendererInner {...props} />
	</GenericErrorBoundary>
);
